import {
  useState,
  useEffect,
  useCallback,
  useRef,
  createContext,
  useContext,
  ReactNode,
} from 'react';
import { env } from '../config/env';
import { getIdentityConfig, resolveBusinessIdentityId } from '../utils/identityRegistry';
import { getLogoutTarget } from '../utils/identityRouting';
import { sectorsFromEntitlements } from '../utils/sectorEntitlements';

/** Public-safe profile snippet from GET /auth/me (UserProfile row). */
export interface UserProfileSummary {
  displayName?: string | null;
  bio?: string | null;
  rewardsPoints?: number;
  badges?: string[];
}

interface User {
  id: string;
  email: string;
  name: string | null;
  role: string;
  sector?: string | null;
  profile?: UserProfileSummary | null;
}

interface Organization {
  id: string;
  name: string;
  sector?: string | null;
  sectorBrandName?: string | null;
}

interface AuthState {
  user: User | null;
  organization: Organization | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** From GET /auth/me — server-side sector_* entitlements (source of truth) */
  entitlements: string[];
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  signup: (
    email: string,
    password: string,
    name: string,
    organizationName?: string,
    sector?: string,
    options?: { source?: string; requestedTier?: string; studentVerification?: string }
  ) => Promise<void>;
  logout: (options?: { redirectTo?: string | null }) => void;
  refreshToken: () => Promise<void>;
  checkSession: (bearerToken?: string) => Promise<boolean>;
  loginLoading: boolean;
  /** Demo mode for investor evaluation — full platform access with hardcoded data */
  isDemoMode: boolean;
  /** Launch full-platform investor demo session */
  demoLogin: () => void;
  /** Launch sector-focused demo session (also grants cross-sector access) */
  sectorDemoLogin: (sectorId: string) => void;
  /** Currently active demo sector (null = full-platform demo) */
  demoSector: string | null;
  /** Currently active sector (works for both demo AND real users) */
  userSector: string | null;
  /** Retrieve the last session snapshot (survives logout) */
  getSessionSnapshot: () => {
    user: User | null;
    organization: Organization | null;
    lastSector: string | null;
    lastLogout: string | null;
    lastView: string | null;
  } | null;
  /** Last user email for quick re-login */
  lastUserEmail: string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Dual-mode auth: httpOnly cookies (preferred) + Bearer token fallback in localStorage
// Bearer fallback is needed when the backend hasn&apos;t deployed cookie-based OAuth yet
const TOKEN_KEY = 'anchor_auth_token';
const USER_KEY = 'anchor_auth_user';
const ORG_KEY = 'anchor_auth_org';
const DEMO_KEY = 'anchor_demo_mode';
const DEMO_SECTOR_KEY = 'anchor_demo_sector';
const USER_SECTOR_KEY = 'anchor_user_sector'; // Persists real user's sector across refreshes
const ENTITLEMENTS_KEY = 'anchor_entitlements';

// Session persistence keys — these survive logout so the customer
// experience is preserved on re-login (last view, preferences, etc.)
const SESSION_SNAPSHOT_KEY = 'anchor_session_snapshot';
const LAST_USER_KEY = 'anchor_last_user';

// ═══ DEMO MODE — Investor Full Platform Access ═══════════════════════
// Controlled demo sessions for investor demonstrations.
// Demo users get full UI access with hardcoded data across all 42 sectors.
// No real database access — all sector dashboards use constant arrays.

function getStoredAuth(): {
  token: string | null;
  user: User | null;
  organization: Organization | null;
} {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const userStr = localStorage.getItem(USER_KEY);
    const orgStr = localStorage.getItem(ORG_KEY);

    return {
      token,
      user: userStr ? JSON.parse(userStr) : null,
      organization: orgStr ? JSON.parse(orgStr) : null,
    };
  } catch {
    return { token: null, user: null, organization: null };
  }
}

function getStoredEntitlements(): string[] {
  try {
    const raw = localStorage.getItem(ENTITLEMENTS_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function setStoredEntitlements(entitlements: string[]) {
  if (entitlements.length > 0) {
    localStorage.setItem(ENTITLEMENTS_KEY, JSON.stringify(entitlements));
    try {
      localStorage.setItem(
        'anchor_registered_sectors',
        JSON.stringify(sectorsFromEntitlements(entitlements))
      );
    } catch {
      /* ignore */
    }
  } else {
    localStorage.removeItem(ENTITLEMENTS_KEY);
  }
}

function setStoredAuth(
  user: User | null,
  organization: Organization | null,
  token?: string | null
) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  }
  if (user) {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } else {
    localStorage.removeItem(USER_KEY);
  }

  if (organization) {
    localStorage.setItem(ORG_KEY, JSON.stringify(organization));
  } else {
    localStorage.removeItem(ORG_KEY);
  }
}

/** Detect persisted demo investor / sector sessions (no real backend identity). */
function isDemoUserProfile(user: User | null): boolean {
  if (!user) return false;
  return user.id.startsWith('demo-');
}

function clearStoredAuth() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(ORG_KEY);
  localStorage.removeItem(ENTITLEMENTS_KEY);
  localStorage.removeItem(DEMO_KEY);
  localStorage.removeItem(DEMO_SECTOR_KEY);
  localStorage.removeItem(USER_SECTOR_KEY);
  // NOTE: SESSION_SNAPSHOT_KEY and LAST_USER_KEY are intentionally
  // NOT cleared — they persist across logout so that customer
  // details, last sector, and session state are preserved for re-login
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(() => {
    // Sovereign live (typical production build): strip demo flags and demo-only users — real credentials only
    if (env.sovereignLiveOnly) {
      try {
        localStorage.removeItem(DEMO_KEY);
        localStorage.removeItem(DEMO_SECTOR_KEY);
      } catch {
        /* ignore */
      }
      const { user, organization } = getStoredAuth();
      if (user && isDemoUserProfile(user)) {
        clearStoredAuth();
        return {
          user: null,
          organization: null,
          token: null,
          entitlements: [],
          isAuthenticated: false,
          isLoading: false,
        };
      }
      const hasCachedUser = !!user;
      return {
        user: hasCachedUser ? user : null,
        organization: hasCachedUser ? organization : null,
        token: null,
        entitlements: getStoredEntitlements(),
        isAuthenticated: false,
        isLoading: hasCachedUser,
      };
    }

    const { user, organization } = getStoredAuth();
    const hasCachedUser = !!user;
    return {
      user: hasCachedUser ? user : null,
      organization: hasCachedUser ? organization : null,
      token: null,
      entitlements: getStoredEntitlements(),
      // Never trust localStorage alone — always start as unauthenticated
      // Token will be verified on mount before granting access
      isAuthenticated: false,
      isLoading: hasCachedUser, // Will verify on mount
    };
  });

  // Separate loading state for login/signup (doesn&apos;t affect global authLoading)
  const [loginLoading, setLoginLoading] = useState(false);

  // ═══ DEMO MODE — Investor Full Platform Access ═══════════════════════════
  const [isDemoActive, setIsDemoActive] = useState(false);
  const [activeDemoSector, setActiveDemoSector] = useState<string | null>(null);

  const demoLogin = useCallback(() => {
    if (env.sovereignLiveOnly) {
      try {
        console.warn('[Anchor] Demo login disabled — sovereign live mode (production build).');
      } catch {
        /* ignore */
      }
      return;
    }
    const demoUser: User = {
      id: 'demo-investor-001',
      email: 'investor@anchor-demo.com',
      name: 'Investor Demo — Full Platform',
      role: 'admin',
    };
    const demoOrg: Organization = {
      id: 'demo-org-001',
      name: 'Anchor AI Guard — Full Platform Demo',
    };
    localStorage.setItem(DEMO_KEY, 'true');
    localStorage.removeItem(DEMO_SECTOR_KEY);
    setStoredAuth(demoUser, demoOrg);
    setIsDemoActive(true);
    setActiveDemoSector(null);
    setStoredEntitlements([]);
    setState({
      user: demoUser,
      organization: demoOrg,
      token: null,
      entitlements: [],
      isAuthenticated: true,
      isLoading: false,
    });
  }, []);

  const sectorDemoLogin = useCallback((sectorId: string) => {
    if (env.sovereignLiveOnly) {
      try {
        console.warn(
          '[Anchor] Sector demo login disabled — sovereign live mode (production build).'
        );
      } catch {
        /* ignore */
      }
      return;
    }
    const resolvedSectorId = resolveBusinessIdentityId(sectorId) ?? sectorId;
    const sectorName = getIdentityConfig(resolvedSectorId)?.label || resolvedSectorId;
    const demoUser: User = {
      id: `demo-${resolvedSectorId}-001`,
      email: `demo@anchor-${resolvedSectorId}.com`,
      name: `${sectorName} — Demo`,
      role: 'admin',
    };
    const demoOrg: Organization = {
      id: `demo-org-${resolvedSectorId}`,
      name: `Anchor ${sectorName}`,
    };
    localStorage.setItem(DEMO_KEY, 'true');
    localStorage.setItem(DEMO_SECTOR_KEY, resolvedSectorId);
    setStoredAuth(demoUser, demoOrg);
    setIsDemoActive(true);
    setActiveDemoSector(resolvedSectorId);
    setStoredEntitlements([]);
    setState({
      user: demoUser,
      organization: demoOrg,
      token: null,
      entitlements: [],
      isAuthenticated: true,
      isLoading: false,
    });
  }, []);

  // Ref to abort verifyToken if login() is called while verify is in-flight
  const verifyAbortRef = useRef<AbortController | null>(null);

  // Restore demo mode from previous session (persist across refreshes)
  useEffect(() => {
    if (env.sovereignLiveOnly) return;
    if (localStorage.getItem(DEMO_KEY) === 'true') {
      const { user, organization } = getStoredAuth();
      if (user) {
        const sector = localStorage.getItem(DEMO_SECTOR_KEY);
        setIsDemoActive(true);
        setActiveDemoSector(sector);
        setState({
          user,
          organization,
          token: null,
          entitlements: [],
          isAuthenticated: true,
          isLoading: false,
        });
        return; // Skip token verification for demo mode
      }
      // Demo flag but no user — corrupted state, clear it
      localStorage.removeItem(DEMO_KEY);
      localStorage.removeItem(DEMO_SECTOR_KEY);
      clearStoredAuth();
    }
  }, []);

  // Verify token on mount — sovereign auth only (skip in demo mode)
  useEffect(() => {
    if (localStorage.getItem(DEMO_KEY) === 'true') return; // Demo mode — no real token
    const { user } = getStoredAuth();
    if (user) {
      verifyToken();
    } else {
      setState((prev) => ({ ...prev, isLoading: false }));
    }
    // Background warmup ping — wake the backend while the user reads the login screen
    fetch(`${env.apiBaseUrl}/health`, { method: 'GET' }).catch(() => {});
    return () => {
      // Abort any pending verify on unmount
      verifyAbortRef.current?.abort();
    };
  }, []);

  const verifyToken = async (attempt = 1): Promise<void> => {
    const controller = new AbortController();
    verifyAbortRef.current = controller;
    const MAX_RETRIES = 3;

    try {
      // Dual-mode auth: try cookie first, fall back to Bearer token from localStorage
      const storedToken = localStorage.getItem(TOKEN_KEY);
      const fetchOptions: RequestInit = {
        credentials: 'include', // Send httpOnly cookie if available
        signal: controller.signal,
        headers: storedToken ? { Authorization: `Bearer ${storedToken}` } : undefined,
      };

      const response = await fetch(`${env.apiBaseUrl}/auth/me`, fetchOptions);

      if (controller.signal.aborted) return;

      if (response.status === 401 || response.status === 403) {
        // Cookie is invalid/expired — clear cached UI data
        clearStoredAuth();
        setState({
          user: null,
          organization: null,
          token: null,
          entitlements: [],
          isAuthenticated: false,
          isLoading: false,
        });
        return;
      }

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();

      if (controller.signal.aborted) return;

      const entitlements: string[] = Array.isArray(data.entitlements) ? data.entitlements : [];
      setStoredEntitlements(entitlements);
      setState((prev) => ({
        ...prev,
        user: data.user,
        organization: data.organization,
        entitlements,
        isAuthenticated: true,
        isLoading: false,
      }));
      setStoredAuth(data.user, data.organization);
    } catch (err) {
      if (controller.signal.aborted) return;

      // Network error or server error (not 401) — backend might be cold-starting
      // Retry with exponential backoff before giving up
      if (attempt < MAX_RETRIES) {
        const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
        await new Promise((resolve) => setTimeout(resolve, delay));
        if (!controller.signal.aborted) {
          return verifyToken(attempt + 1);
        }
        return;
      }

      // After all retries failed: clear auth and require re-login
      // Never trust cached data — if backend can&apos;t verify cookie, user must re-authenticate
      clearStoredAuth();
      setState({
        user: null,
        organization: null,
        token: null,
        entitlements: [],
        isAuthenticated: false,
        isLoading: false,
      });
    }
  };

  const login = useCallback(async (email: string, password: string) => {
    // Abort any in-flight token verification to prevent it from clobbering this login
    verifyAbortRef.current?.abort();

    setLoginLoading(true);

    // Warm up backend if it's cold-starting
    try {
      const warmup = await fetch(`${env.apiBaseUrl}/health`, { method: 'GET' });
      if (!warmup.ok) {
        // Backend responded but isn't healthy yet — give it a moment
        await new Promise((r) => setTimeout(r, 2000));
      }
    } catch {
      // Backend unreachable — wait for cold start then proceed with retries
      await new Promise((r) => setTimeout(r, 3000));
    }

    // Retry logic for backend cold starts — 5 attempts with exponential backoff
    const MAX_ATTEMPTS = 5;
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      try {
        const response = await fetch(`${env.apiBaseUrl}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include', // SECURITY: Receive and store httpOnly cookie
          body: JSON.stringify({ email, password }),
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({ error: 'Login failed' }));
          // Don&apos;t retry on auth errors (wrong credentials, rate limit, etc.)
          if (
            response.status === 401 ||
            response.status === 409 ||
            response.status === 429 ||
            response.status === 400
          ) {
            setLoginLoading(false);
            throw new Error(error.error || 'Login failed');
          }
          // Server error — retry
          throw new Error(`Server error (${response.status})`);
        }

        const data = await response.json();

        // SECURITY: Token is set via httpOnly cookie by the server — never stored in JS
        setStoredAuth(data.user, data.organization);

        // Store sector from backend response for post-login routing
        const userSector =
          resolveBusinessIdentityId(data.user?.sector || data.organization?.sector) ||
          data.user?.sector ||
          data.organization?.sector ||
          null;
        if (userSector) {
          localStorage.setItem(USER_SECTOR_KEY, userSector);
        }

        setLoginLoading(false);
        setState({
          user: data.user,
          organization: data.organization,
          token: null, // Token lives in httpOnly cookie only
          entitlements: [],
          isAuthenticated: true,
          isLoading: false,
        });
        try {
          const meRes = await fetch(`${env.apiBaseUrl}/auth/me`, { credentials: 'include' });
          if (meRes.ok) {
            const me = await meRes.json();
            const entitlements: string[] = Array.isArray(me.entitlements) ? me.entitlements : [];
            setStoredEntitlements(entitlements);
            setState((prev) => ({
              ...prev,
              user: me.user,
              organization: me.organization,
              entitlements,
            }));
          }
        } catch {
          /* ignore */
        }
        return; // Success — exit
      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Login failed');
        // If it&apos;s a known auth error (thrown above with specific status), don&apos;t retry
        if (
          lastError.message.includes('Invalid') ||
          lastError.message.includes('already exists') ||
          lastError.message.includes('rate') ||
          lastError.message === 'Login failed'
        ) {
          setLoginLoading(false);
          throw lastError;
        }
        // Network/server error — retry with exponential backoff
        if (attempt < MAX_ATTEMPTS) {
          await new Promise((resolve) =>
            setTimeout(resolve, Math.min(1500 * Math.pow(2, attempt - 1), 8000))
          );
        }
      }
    }

    setLoginLoading(false);
    throw new Error(
      lastError?.message?.includes('Server error')
        ? 'Server is waking up — please wait a moment and try again'
        : 'Unable to connect to server. Please check your internet connection and try again.'
    );
  }, []);

  const signup = useCallback(
    async (
      email: string,
      password: string,
      name: string,
      organizationName?: string,
      sector?: string,
      options?: { source?: string; requestedTier?: string; studentVerification?: string }
    ) => {
      setLoginLoading(true);

      const MAX_ATTEMPTS = 3;
      let lastError: Error | null = null;

      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
          const response = await fetch(`${env.apiBaseUrl}/auth/signup`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include', // SECURITY: Receive and store httpOnly cookie
            body: JSON.stringify({
              email,
              password,
              name,
              organizationName,
              sector,
              source: options?.source,
              requestedTier: options?.requestedTier,
              studentVerification: options?.studentVerification,
            }),
          });

          if (!response.ok) {
            const error = (await response.json().catch(() => ({ error: 'Signup failed' }))) as {
              error?: string;
              message?: string;
              details?: { fieldErrors?: Record<string, string[] | string> };
            };
            if (response.status === 400 || response.status === 409 || response.status === 429) {
              const fieldErrors = (error?.details?.fieldErrors ?? {}) as Record<
                string,
                string[] | string | undefined
              >;
              const flat: string[] = [];
              for (const v of Object.values(fieldErrors)) {
                if (Array.isArray(v)) flat.push(...v);
                else if (typeof v === 'string') flat.push(v);
              }
              const firstFieldError = flat.find(Boolean);
              const friendly =
                firstFieldError ||
                (typeof error.message === 'string' && error.message.trim()
                  ? error.message
                  : undefined) ||
                (typeof error.error === 'string' && error.error.trim() ? error.error : undefined) ||
                'Signup failed';
              setLoginLoading(false);
              throw new Error(friendly);
            }
            throw new Error(`Server error (${response.status})`);
          }

          const data = await response.json();

          // SECURITY: Token is set via httpOnly cookie by the server — never stored in JS
          setStoredAuth(data.user, data.organization);

          // Store sector from backend response for post-signup routing
          const userSector =
            resolveBusinessIdentityId(data.user?.sector || data.organization?.sector || sector) ||
            data.user?.sector ||
            data.organization?.sector ||
            sector ||
            null;
          if (userSector) {
            localStorage.setItem(USER_SECTOR_KEY, userSector);
          }

          setLoginLoading(false);
          setState({
            user: data.user,
            organization: data.organization,
            token: null, // Token lives in httpOnly cookie only
            entitlements: [],
            isAuthenticated: true,
            isLoading: false,
          });
          try {
            const meRes = await fetch(`${env.apiBaseUrl}/auth/me`, { credentials: 'include' });
            if (meRes.ok) {
              const me = await meRes.json();
              const entitlements: string[] = Array.isArray(me.entitlements) ? me.entitlements : [];
              setStoredEntitlements(entitlements);
              setState((prev) => ({
                ...prev,
                user: me.user,
                organization: me.organization,
                entitlements,
              }));
            }
          } catch {
            /* ignore */
          }
          return; // Success
        } catch (error) {
          lastError = error instanceof Error ? error : new Error('Signup failed');
          if (
            lastError.message.includes('already exists') ||
            lastError.message.includes('Invalid') ||
            lastError.message === 'Signup failed'
          ) {
            setLoginLoading(false);
            throw lastError;
          }
          if (attempt < MAX_ATTEMPTS) {
            await new Promise((resolve) =>
              setTimeout(resolve, Math.min(1500 * Math.pow(2, attempt - 1), 8000))
            );
          }
        }
      }

      setLoginLoading(false);
      throw new Error(
        lastError?.message?.includes('Server error')
          ? 'Server is waking up — please wait a moment and try again'
          : 'Unable to connect to server. Please check your internet connection and try again.'
      );
    },
    []
  );

  const logout = useCallback((options?: { redirectTo?: string | null }) => {
    // ── Capture ALL session data BEFORE clearing anything ──
    // This ensures the snapshot has complete customer context for seamless re-login
    const preClearUser = localStorage.getItem(USER_KEY);
    const preClearOrg = localStorage.getItem(ORG_KEY);
    const preClearDemoSector = localStorage.getItem(DEMO_SECTOR_KEY);
    const preClearUserSector = localStorage.getItem(USER_SECTOR_KEY);
    const preClearDemoMode = localStorage.getItem(DEMO_KEY) === 'true';
    const parsedOrg = preClearOrg ? (JSON.parse(preClearOrg) as Organization) : null;
    const logoutTarget =
      options?.redirectTo ||
      getLogoutTarget({
        currentPath: window.location.pathname,
        demoSector: preClearDemoSector,
        userSector: preClearUserSector,
        organizationSector: parsedOrg?.sector || null,
      }).path;

    // Clear demo state
    setIsDemoActive(false);
    setActiveDemoSector(null);
    localStorage.removeItem(DEMO_KEY);
    localStorage.removeItem(DEMO_SECTOR_KEY);
    localStorage.removeItem('anchor_signup_sector');
    localStorage.removeItem('business_setup_complete');

    // ── Save session snapshot with pre-captured data ──
    // Preserves customer details, last active sector, org, subscription context
    // so the experience is seamless on re-login to their business
    try {
      const snapshot = {
        user: preClearUser ? JSON.parse(preClearUser) : null,
        organization: preClearOrg ? JSON.parse(preClearOrg) : null,
        lastSector: preClearUserSector || preClearDemoSector || null,
        lastDemoMode: preClearDemoMode,
        lastLogout: new Date().toISOString(),
        // Capture current URL path as last view
        lastView: window.location.pathname + window.location.hash,
      };
      localStorage.setItem(SESSION_SNAPSHOT_KEY, JSON.stringify(snapshot));
      // Also save last user email for quick re-login
      if (snapshot.user?.email) {
        localStorage.setItem(LAST_USER_KEY, snapshot.user.email);
      }
    } catch {
      /* Best-effort snapshot — don't block logout */
    }

    // SECURITY: Tell server to clear the httpOnly cookie and persist session data
    fetch(`${env.apiBaseUrl}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    }).catch(() => {
      /* Best-effort — cookie will expire naturally */
    });
    clearStoredAuth();
    setState({
      user: null,
      organization: null,
      token: null,
      entitlements: [],
      isAuthenticated: false,
      isLoading: false,
    });
    // After callers finish (e.g. `logout(); onNavigate('anchor-…')`), land on login — not marketing or sector landing.
    setTimeout(() => {
      window.history.pushState({}, '', logoutTarget);
      window.dispatchEvent(new PopStateEvent('popstate'));
    }, 0);
  }, []);

  const refreshToken = useCallback(async () => {
    try {
      const storedToken = localStorage.getItem(TOKEN_KEY);
      const response = await fetch(`${env.apiBaseUrl}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: storedToken ? { Authorization: `Bearer ${storedToken}` } : undefined,
      });

      if (response.status === 401 || response.status === 403) {
        // Cookie is explicitly invalid — log out
        logout();
        return;
      }

      if (!response.ok) {
        // Server error — don&apos;t log out, cookie might still be valid
        return;
      }

      // New cookie is set by the server response automatically
    } catch {
      // Network error — don&apos;t log out the user
      // Cookie might still be valid, backend is just temporarily unreachable
    }
  }, [logout]);

  // Verify session and update auth state in-place.
  // Used by OAuth callback to set auth without a full page reload.
  // Supports dual-mode: cookie auth (no bearerToken) or Bearer token fallback.
  const checkSession = useCallback(async (bearerToken?: string): Promise<boolean> => {
    // Abort any pending verifyToken to prevent race conditions
    verifyAbortRef.current?.abort();

    try {
      const fetchOptions: RequestInit = {
        credentials: 'include',
        headers: bearerToken ? { Authorization: `Bearer ${bearerToken}` } : undefined,
      };
      const response = await fetch(`${env.apiBaseUrl}/auth/me`, fetchOptions);

      if (!response.ok) {
        clearStoredAuth();
        setState({
          user: null,
          organization: null,
          token: null,
          entitlements: [],
          isAuthenticated: false,
          isLoading: false,
        });
        return false;
      }

      const data = await response.json();
      // Store token if provided (OAuth token-in-URL fallback)
      setStoredAuth(data.user, data.organization, bearerToken);
      const entitlements: string[] = Array.isArray(data.entitlements) ? data.entitlements : [];
      setStoredEntitlements(entitlements);
      setState({
        user: data.user,
        organization: data.organization,
        token: null,
        entitlements,
        isAuthenticated: true,
        isLoading: false,
      });
      return true;
    } catch {
      return false;
    }
  }, []);

  const getSessionSnapshot = useCallback(() => {
    try {
      const raw = localStorage.getItem(SESSION_SNAPSHOT_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const lastUserEmail = localStorage.getItem(LAST_USER_KEY);

  // Compute the active user sector — works for both real and demo users
  const userSector: string | null =
    resolveBusinessIdentityId(
      state.user?.sector || state.organization?.sector || localStorage.getItem(USER_SECTOR_KEY)
    ) ||
    state.user?.sector ||
    state.organization?.sector ||
    localStorage.getItem(USER_SECTOR_KEY) ||
    null;

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        signup,
        logout,
        refreshToken,
        checkSession,
        loginLoading,
        isDemoMode: isDemoActive && !env.sovereignLiveOnly,
        demoLogin,
        sectorDemoLogin,
        demoSector: activeDemoSector,
        userSector,
        getSessionSnapshot,
        lastUserEmail,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Hook for making authenticated API calls
export function useAuthenticatedFetch() {
  const { logout } = useAuth();

  return useCallback(
    async (url: string, options: RequestInit = {}) => {
      const headers = new Headers(options.headers);

      // Dual-mode: send Bearer token if available (fallback for pre-cookie backend)
      const storedToken = localStorage.getItem(TOKEN_KEY);
      if (storedToken && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${storedToken}`);
      }
      if (!headers.has('Content-Type') && options.body) {
        headers.set('Content-Type', 'application/json');
      }

      const response = await fetch(url, {
        ...options,
        headers,
        credentials: 'include', // Also send httpOnly cookie when available
      });

      if (response.status === 401) {
        logout();
        throw new Error('Session expired');
      }

      return response;
    },
    [logout]
  );
}
